import React, { useEffect, useState } from 'react';
import { Box, CssBaseline, Drawer, GlobalStyles, useMediaQuery, useTheme } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import SidebarNav, { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from '../Components/Navigation/SidebarNav';
import AppHeader, { HEADER_HEIGHT } from '../Components/Navigation/AppHeader';
import Footer from '../Components/Elements/CopyrightFooter';
import { appColors } from '../Config/Theme';

export default function AppShell() {
  const { pathname } = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Auto-collapse sidebar only in narrow tablet portrait (900–1023px); keep full sidebar at iPad landscape (≥1024px)
  const isTablet = useMediaQuery('(min-width: 900px) and (max-width: 1023px)');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  useEffect(() => { setMobileDrawerOpen(false); }, [pathname]);
  const effectiveCollapsed = sidebarCollapsed || isTablet;
  const currentSidebarWidth = effectiveCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  return (
    <>
      <CssBaseline />
      <GlobalStyles
        styles={{
          'html, body, #root': { height: '100%', margin: 0, padding: 0, overflowX: 'hidden' },
          '#root': { display: 'flex', flexDirection: 'column' },
          'html': { WebkitTextSizeAdjust: '100%' },
        }}
      />

      {/* Fixed top header */}
      <AppHeader onMenuToggle={() => setMobileDrawerOpen((o) => !o)} />

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          slotProps={{
            paper: {
              sx: {
                width: SIDEBAR_WIDTH,
                mt: `${HEADER_HEIGHT}px`,
                height: `calc(100% - ${HEADER_HEIGHT}px)`,
              },
            },
          }}
          ModalProps={{ keepMounted: true }}
        >
          <SidebarNav collapsed={false} onToggle={() => setMobileDrawerOpen(false)} />
        </Drawer>
      )}

      {/* Fixed sidebar — desktop only, below header */}
      {!isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: HEADER_HEIGHT,
            left: 0,
            bottom: 0,
            width: currentSidebarWidth,
            zIndex: 1200,
            transition: 'width 0.2s ease',
          }}
        >
          <SidebarNav collapsed={effectiveCollapsed} onToggle={() => setSidebarCollapsed((c) => !c)} />
        </Box>
      )}

      {/* Scrollable content area — offset by header height + sidebar width (desktop) */}
      <Box
        sx={{
          '--sidebar-w': isMobile ? '0px' : `${currentSidebarWidth}px`,
          ml: { xs: 0, md: `${currentSidebarWidth}px` },
          mt: `${HEADER_HEIGHT}px`,
          display: 'flex',
          flexDirection: 'column',
          minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
          minWidth: 0,
          maxWidth: { xs: '100%', md: `calc(100vw - ${currentSidebarWidth}px)` },
          overflowX: 'hidden',
          transition: 'margin-left 0.2s ease, max-width 0.2s ease',
        }}
      >
        <Box
          component="main"
          sx={{
            flex: 1,
            bgcolor: appColors.surface.page,
          }}
        >
          <Outlet />
        </Box>
        <Footer />
      </Box>
    </>
  );
}
